import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { 
  Plus, 
  CircleNotch,
  Trash,
  DownloadSimple,
  Eye,
  FileText,
  FilePdf,
  FileDoc,
  FileXls,
  FilePpt,
  FileImage,
  FileCsv,
  File,
  CloudArrowUp,
  Funnel,
  MagnifyingGlass,
  DotsThreeVertical,
  FolderOpen,
  HardDrive,
  Tag,
  Clock
} from '@phosphor-icons/react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'documents', label: 'Documents' },
  { value: 'compliance', label: 'Compliance' },
  { value: 'invoices', label: 'Invoices' },
  { value: 'employees', label: 'Employees' },
  { value: 'contracts', label: 'Contracts' },
  { value: 'reports', label: 'Reports' },
];

const getFileIcon = (fileType, mimeType) => {
  if (fileType === 'image') return FileImage;
  if (mimeType?.includes('pdf')) return FilePdf;
  if (mimeType?.includes('word') || mimeType?.includes('.doc')) return FileDoc;
  if (mimeType?.includes('excel') || mimeType?.includes('spreadsheet') || mimeType?.includes('.xls')) return FileXls;
  if (mimeType?.includes('powerpoint') || mimeType?.includes('presentation') || mimeType?.includes('.ppt')) return FilePpt;
  if (mimeType?.includes('csv')) return FileCsv;
  if (mimeType?.includes('text')) return FileText;
  return File;
};

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export default function StoragePage() {
  const { user, hasPermission } = useAuth();
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileInputRef = useRef(null);
  
  // Filters
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Upload form
  const [uploadForm, setUploadForm] = useState({
    file: null,
    category: 'general',
    description: '',
    tags: ''
  });

  const canCreate = hasPermission('hr', 'create');
  const canDelete = hasPermission('hr', 'delete');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, docsRes] = await Promise.all([
        axios.get(`${API_URL}/api/storage/stats`),
        axios.get(`${API_URL}/api/storage/documents`)
      ]);
      setStats(statsRes.data);
      setDocuments(docsRes.data);
    } catch (error) {
      console.error('Failed to fetch storage data:', error);
      toast.error('Failed to load storage data');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB');
        return;
      }
      setUploadForm(prev => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('category', uploadForm.category);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.tags) formData.append('tags', uploadForm.tags);

      await axios.post(`${API_URL}/api/storage/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully');
      setDialogOpen(false);
      setUploadForm({ file: null, category: 'general', description: '', tags: '' });
      if (fileInputRef.current) fileInputRef.current.value = '';
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`${API_URL}/api/storage/documents/${docId}`);
      toast.success('Document deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  const handleDownload = (doc) => {
    window.open(`${API_URL}${doc.file_url}`, '_blank');
  };

  const filteredDocuments = documents.filter(doc => {
    if (filterCategory !== 'all' && doc.category !== filterCategory) return false;
    if (filterType !== 'all' && doc.file_type !== filterType) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return doc.filename.toLowerCase().includes(search) ||
             doc.description?.toLowerCase().includes(search) ||
             doc.tags?.some(t => t.toLowerCase().includes(search));
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="storage-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
            <HardDrive size={28} weight="duotone" className="text-primary" />
            Document Storage
          </h1>
          <p className="text-muted-foreground mt-1">Upload, organize, and manage your files</p>
        </div>
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="upload-btn">
                <CloudArrowUp size={16} className="mr-2" />
                Upload File
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Upload Document</DialogTitle>
                <DialogDescription>
                  Upload files up to 10MB (images, PDFs, documents)
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div 
                  className="border-2 border-dashed border-border rounded-lg p-8 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    className="hidden"
                    onChange={handleFileSelect}
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.webp,.svg"
                  />
                  {uploadForm.file ? (
                    <div className="space-y-2">
                      <File size={40} className="mx-auto text-primary" />
                      <p className="font-medium">{uploadForm.file.name}</p>
                      <p className="text-sm text-muted-foreground">{formatFileSize(uploadForm.file.size)}</p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <CloudArrowUp size={40} className="mx-auto text-muted-foreground" />
                      <p className="text-muted-foreground">Click to select a file</p>
                    </div>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select 
                    value={uploadForm.category} 
                    onValueChange={(v) => setUploadForm(p => ({ ...p, category: v }))}
                  >
                    <SelectTrigger data-testid="upload-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-2">
                  <Label>Description (optional)</Label>
                  <Input
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Brief description..."
                    data-testid="upload-description"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Tags (comma separated)</Label>
                  <Input
                    value={uploadForm.tags}
                    onChange={(e) => setUploadForm(p => ({ ...p, tags: e.target.value }))}
                    placeholder="contract, 2024, legal..."
                    data-testid="upload-tags"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleUpload} disabled={uploading || !uploadForm.file} data-testid="confirm-upload-btn">
                  {uploading && <CircleNotch className="animate-spin mr-2" size={16} />}
                  Upload
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Total Files</p>
              <p className="text-2xl font-bold mt-1">{stats?.total_documents || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-primary/10">
              <FolderOpen size={24} className="text-primary" weight="duotone" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Storage Used</p>
              <p className="text-2xl font-bold mt-1">{stats?.total_size_formatted || '0 MB'}</p>
            </div>
            <div className="p-3 rounded-full bg-blue-500/10">
              <HardDrive size={24} className="text-blue-600" weight="duotone" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Documents</p>
              <p className="text-2xl font-bold mt-1">{stats?.by_type?.document || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-amber-500/10">
              <FileText size={24} className="text-amber-600" weight="duotone" />
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="card-tactile rounded-lg p-5"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">Images</p>
              <p className="text-2xl font-bold mt-1">{stats?.by_type?.image || 0}</p>
            </div>
            <div className="p-3 rounded-full bg-emerald-500/10">
              <FileImage size={24} className="text-emerald-600" weight="duotone" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Filters and Document List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
        className="card-tactile rounded-lg overflow-hidden"
      >
        {/* Filters */}
        <div className="p-4 border-b border-border bg-muted/30">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex-1 relative">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search files..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 max-w-sm"
                data-testid="storage-search"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-36" data-testid="filter-category">
                  <Funnel size={14} className="mr-2" />
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32" data-testid="filter-type">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="document">Documents</SelectItem>
                  <SelectItem value="image">Images</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Document Grid */}
        <div className="p-4">
          {filteredDocuments.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">No documents found</p>
              {canCreate && (
                <Button variant="outline" className="mt-4" onClick={() => setDialogOpen(true)}>
                  <CloudArrowUp size={16} className="mr-2" />
                  Upload your first file
                </Button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filteredDocuments.map(doc => {
                const FileIcon = getFileIcon(doc.file_type, doc.mime_type);
                
                return (
                  <div 
                    key={doc.id} 
                    className="group border border-border rounded-lg p-4 hover:shadow-md transition-all bg-card"
                    data-testid={`doc-${doc.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="p-2 rounded-lg bg-muted">
                        <FileIcon size={24} className="text-muted-foreground" weight="duotone" />
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-opacity">
                            <DotsThreeVertical size={16} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleDownload(doc)}>
                            <DownloadSimple size={16} className="mr-2" />
                            Download
                          </DropdownMenuItem>
                          {doc.file_type === 'image' && (
                            <DropdownMenuItem onClick={() => setPreviewDoc(doc)}>
                              <Eye size={16} className="mr-2" />
                              Preview
                            </DropdownMenuItem>
                          )}
                          {canDelete && (
                            <DropdownMenuItem 
                              onClick={() => handleDelete(doc.id)}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash size={16} className="mr-2" />
                              Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                    
                    <h4 className="font-medium text-sm truncate mb-1" title={doc.filename}>
                      {doc.filename}
                    </h4>
                    
                    {doc.description && (
                      <p className="text-xs text-muted-foreground truncate mb-2">{doc.description}</p>
                    )}
                    
                    <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                      <span>{formatFileSize(doc.file_size)}</span>
                      <span>•</span>
                      <Badge variant="outline" className="text-xs capitalize">{doc.category}</Badge>
                    </div>
                    
                    {doc.tags?.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {doc.tags.slice(0, 3).map((tag, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            <Tag size={10} className="mr-1" />
                            {tag}
                          </Badge>
                        ))}
                        {doc.tags.length > 3 && (
                          <Badge variant="secondary" className="text-xs">+{doc.tags.length - 3}</Badge>
                        )}
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock size={12} />
                      <span>{new Date(doc.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Category Summary */}
        {stats?.by_category && Object.keys(stats.by_category).length > 0 && (
          <div className="p-4 border-t border-border bg-muted/30">
            <p className="text-sm font-medium mb-3">By Category</p>
            <div className="flex flex-wrap gap-2">
              {Object.entries(stats.by_category).map(([category, data]) => (
                <div key={category} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-background border border-border">
                  <span className="text-sm font-medium capitalize">{category}</span>
                  <Badge variant="outline" className="text-xs">
                    {data.count} files
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    ({formatFileSize(data.size)})
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Image Preview Dialog */}
      {previewDoc && (
        <Dialog open={!!previewDoc} onOpenChange={() => setPreviewDoc(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>{previewDoc.filename}</DialogTitle>
            </DialogHeader>
            <div className="max-h-[70vh] overflow-auto">
              <img 
                src={`${API_URL}${previewDoc.file_url}`} 
                alt={previewDoc.filename}
                className="w-full h-auto rounded-lg"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewDoc(null)}>Close</Button>
              <Button onClick={() => handleDownload(previewDoc)}>
                <DownloadSimple size={16} className="mr-2" />
                Download
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
